
import { useState, useEffect, useMemo } from 'react';
import { Terrace } from '@/types/terrace';
import { useSunStatusUpdater } from '../sun-status';
import { fetchTerraceByIdentifier } from './terraceDataService';
import { UseTerraceDetailReturn, TerraceDetailState } from './types';

export const useTerraceDetail = (identifier: string | undefined): UseTerraceDetailReturn => {
  const [state, setState] = useState<TerraceDetailState>({
    terrace: null,
    loading: true,
    error: null
  });

  // Sun status updater hook with improved configuration
  const sunStatusUpdater = useSunStatusUpdater({
    terraceId: state.terrace?.id || '',
    currentStatus: state.terrace?.sunStatus || 'not-sunny',
    lastUpdated: state.terrace?.sun_status_last_updated,
    options: {
      autoUpdateThresholdMinutes: 30, // Update if data is older than 30 minutes
      rateLimit: {
        autoUpdateMinutes: 10, // Auto-updates limited to every 10 minutes
        manualUpdateMinutes: 2  // Manual updates limited to every 2 minutes
      }
    }
  });

  // Memoize the enhanced terrace to prevent unnecessary re-renders
  const enhancedTerrace = useMemo(() => {
    if (!state.terrace) return null;
    
    return {
      ...state.terrace,
      sunStatus: sunStatusUpdater.updatedStatus,
      sun_status_last_updated: sunStatusUpdater.lastChecked
    };
  }, [state.terrace, sunStatusUpdater.updatedStatus, sunStatusUpdater.lastChecked]);

  useEffect(() => {
    const fetchTerrace = async () => {
      if (!identifier) {
        setState({
          terrace: null,
          loading: false,
          error: 'No terrace identifier provided'
        });
        return;
      }

      setState(prev => ({ ...prev, loading: true, error: null }));
      
      try {
        const terrace = await fetchTerraceByIdentifier(identifier);
        setState({
          terrace,
          loading: false,
          error: null
        });
      } catch (err) {
        console.error('Error in fetchTerrace:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load terrace';
        setState({
          terrace: null,
          loading: false,
          error: errorMessage
        });
      }
    };

    fetchTerrace();
  }, [identifier]);

  return { 
    terrace: enhancedTerrace, 
    loading: state.loading || sunStatusUpdater.isUpdating, 
    error: state.error || sunStatusUpdater.error,
    isSunStatusUpdating: sunStatusUpdater.isUpdating,
    triggerSunStatusUpdate: sunStatusUpdater.triggerUpdate,
    canAutoUpdate: sunStatusUpdater.canAutoUpdate,
    updateConditions: sunStatusUpdater.updateConditions
  };
};
